<template>
  <v-container>
    <v-row>
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center mb-4">
          <h1 class="text-h4 mb-0">
            {{ $t('rates.title') }}
            <span v-if="selectedCoinId" class="text-caption text-grey ml-2">
              {{ $t('rates.filteredByCoin') }}
            </span>
          </h1>
          <div class="d-flex align-center">
            <v-autocomplete
              v-model="selectedCoinId"
              :items="coinOptions"
              item-title="label"
              item-value="id"
              :label="$t('rates.filterByCoin')"
              class="mr-4"
              clearable
              density="compact"
              hide-details
              style="min-width: 240px;"
              @update:model-value="handleCoinChange"
            />
            <v-btn 
              v-if="selectedCoinId" 
              variant="outlined"
              prepend-icon="mdi-arrow-left"
              class="mr-2" 
              @click="router.push('/coins')"
            >
              {{ $t('rates.coins') }}
            </v-btn>
            <v-btn 
              v-if="selectedCoinId" 
              prepend-icon="mdi-arrow-left"
              variant="outlined" 
              class="mr-2" 
              @click="router.push('/rates')"
            >
            {{ $t('rates.rates') }}
            </v-btn>
            <v-btn color="primary" class="mr-2" @click="openAddRateDialog">
              {{ $t('rates.add') }}
            </v-btn>
            <v-btn color="primary" @click="fetchRatesForAllCoins" :loading="loadingRates">
              {{ $t('rates.getRates') }}
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>
    
    <v-row>
      <v-col cols="12">
        <v-table density="compact" class="rates-table">
          <thead>
            <tr>
              <th>{{ $t('rates.coin') }}</th>
              <th>{{ $t('rates.rateUsdt') }}</th>
              <th>{{ $t('rates.timestamp') }}</th>
              <th>{{ $t('common.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="rate in coinRates" :key="rate.id">
              <tr>
                <td 
                  style="cursor: pointer;"
                  @click="goToCoinRates(rate.coin_id)"
                  class="coin-name-cell"
                >
                  {{ rate.coin_symbol }} - {{ rate.coin_name }}
                </td>
                <td class="text-right">{{ rate.rate }}</td>
                <td>{{ new Date(rate.timestamp).toLocaleString() }}</td>
                <td @click.stop>
                  <v-btn variant="plain" size="small" icon="mdi-pencil" @click="openEditRateDialog(rate)"></v-btn>
                  <v-btn variant="plain" size="small" color="error" @click="deleteRate(rate.id)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>
            <tr v-if="coinRates.length === 0">
              <td colspan="4" class="text-center text-grey">{{ $t('rates.noRatesFound') }}</td>
            </tr>
          </tbody>
        </v-table>
      </v-col>
    </v-row>
  </v-container>

  <v-dialog v-model="showRateDialog" max-width="500" @update:model-value="(val) => { if (!val) resetRateForm() }">
    <v-card>
      <v-card-title>{{ rateForm.id ? $t('rates.edit') : $t('rates.add') }}</v-card-title>
      <v-card-text>
        <v-form>
          <v-select
            v-model="rateForm.coin_id"
            :items="coinOptions"
            item-title="label"
            item-value="id"
            :label="$t('rates.coin')"
            :disabled="!!selectedCoinId && !rateForm.id"
            density="compact"
            clearable
          />
          <v-text-field
            v-model="rateForm.rate"
            :label="$t('rates.rateUsdt')"
            type="number"
            density="compact"
            class="mt-3"
          />
          <v-text-field
            v-model="rateForm.timestamp"
            :label="$t('rates.timestamp')"
            type="datetime-local"
            density="compact"
            class="mt-3"
          />
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn @click="showRateDialog = false">{{ $t('common.cancel') }}</v-btn>
        <v-btn color="primary" @click="saveRate">{{ $t('common.save') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { apiGet, apiPost, apiDelete, apiPut } from '../utils/api'

const route = useRoute()
const router = useRouter()
const { t } = useI18n()

interface CoinRate {
  id: number
  coin_id: number
  coin_symbol: string
  coin_name: string
  rate: number
  timestamp: string
}

const coinRates = ref<CoinRate[]>([])
const loadingRates = ref(false)
const selectedCoinId = ref<number | null>(null)
const coins = ref<any[]>([])
const coinOptions = ref<Array<{ id: number; label: string }>>([])
const showRateDialog = ref(false)
const rateForm = ref({
  id: null as number | null,
  coin_id: null as number | null,
  rate: null as number | null,
  timestamp: ''
})

const loadRates = async () => {
  try {
    let url = '/api/coin_rates'
    if (selectedCoinId.value) {
      url += `?coin_id=${selectedCoinId.value}`
    }
    const response = await apiGet(url)
    if (response.ok) {
      coinRates.value = await response.json()
    }
  } catch (error) {
    console.error('Load rates error:', error)
  }
}

const loadCoins = async () => {
  try {
    const response = await apiGet('/api/coins')
    if (response.ok) {
      const data = await response.json()
      coins.value = data
      coinOptions.value = data.map((c: any) => ({
        id: c.id,
        label: `${c.symbol} - ${c.name}`
      }))
    }
  } catch (error) {
    console.error('Load coins error:', error)
  }
}

// Watch for coin_id query parameter
watch(() => route.query.coin_id, (coinId) => {
  if (coinId) {
    selectedCoinId.value = parseInt(coinId as string)
  } else {
    selectedCoinId.value = null
  }
  loadRates()
}, { immediate: true })

// Also watch route changes to catch navigation
watch(() => route.fullPath, () => {
  if (route.query.coin_id) {
    selectedCoinId.value = parseInt(route.query.coin_id as string)
  } else {
    selectedCoinId.value = null
  }
})

const fetchRatesForAllCoins = async () => {
  loadingRates.value = true
  try {
    let coinSymbols: string[] = []
    
    // If there's a filter, only fetch rates for the selected coin
    if (selectedCoinId.value) {
      const selectedCoin = coins.value.find((c: any) => c.id === selectedCoinId.value)
      if (!selectedCoin) {
        // If coin not found in local cache, fetch from API
        const coinsResponse = await apiGet('/api/coins')
        if (!coinsResponse.ok) {
          alert(t('rates.failedToLoadCoins'))
          return
        }
        const allCoins = await coinsResponse.json()
        const foundCoin = allCoins.find((c: any) => c.id === selectedCoinId.value)
        if (!foundCoin) {
          alert(t('rates.coinNotFound'))
          return
        }
        coinSymbols = [foundCoin.symbol]
      } else {
        coinSymbols = [selectedCoin.symbol]
      }
    } else {
      // No filter - fetch rates for all coins
      const coinsResponse = await apiGet('/api/coins')
      if (!coinsResponse.ok) {
        alert(t('rates.failedToLoadCoins'))
        return
      }
      
      const allCoins = await coinsResponse.json()
      coinSymbols = allCoins.map((c: any) => c.symbol)
    }
    
    // Fetch rates for the selected coins
    const response = await apiPost('/api/coin_rates/fetch_from_cryptocompare', {
      coin_symbols: coinSymbols
    })
    
    if (response.ok) {
      const result = await response.json()
      if (result.errors && result.errors.length > 0) {
        alert(t('rates.errorsOccurred', { errors: result.errors.join('\n') }))
      } else {
        console.log(`Successfully fetched rates for ${result.saved.length} coins`)
      }
      await loadRates()
    } else {
      const errorData = await response.json()
      alert(t('rates.errorFetching', { error: errorData.error || t('rates.failedToFetch') }))
    }
  } catch (error) {
    console.error('Fetch rates error:', error)
    alert(t('rates.failedToFetch'))
  } finally {
    loadingRates.value = false
  }
}

const goToCoinRates = (coinId: number) => {
  router.push(`/rates?coin_id=${coinId}`)
}

const openAddRateDialog = () => {
  resetRateForm()
  if (selectedCoinId.value) {
    rateForm.value.coin_id = selectedCoinId.value
  }
  showRateDialog.value = true
}

const openEditRateDialog = (rate: CoinRate) => {
  rateForm.value = {
    id: rate.id,
    coin_id: rate.coin_id,
    rate: rate.rate,
    timestamp: toLocalInput(rate.timestamp)
  }
  showRateDialog.value = true
}

const resetRateForm = () => {
  rateForm.value = {
    id: null,
    coin_id: selectedCoinId.value || null,
    rate: null,
    timestamp: toLocalInput()
  }
}

const toLocalInput = (dateString?: string) => {
  const date = dateString ? new Date(dateString) : new Date()
  // Convert to local time and strip seconds for datetime-local
  const tzOffsetMs = date.getTimezoneOffset() * 60000
  const localISO = new Date(date.getTime() - tzOffsetMs).toISOString()
  return localISO.slice(0, 16)
}

const saveRate = async () => {
  if (!rateForm.value.coin_id || !rateForm.value.rate) {
    alert(t('rates.fillCoinRate'))
    return
  }

  const payload = {
    coin_rate: {
      coin_id: rateForm.value.coin_id,
      rate: Number(rateForm.value.rate),
      timestamp: rateForm.value.timestamp ? new Date(rateForm.value.timestamp).toISOString() : new Date().toISOString()
    }
  }

  try {
    let response
    if (rateForm.value.id) {
      response = await apiPut(`/api/coin_rates/${rateForm.value.id}`, payload)
    } else {
      response = await apiPost('/api/coin_rates', payload)
    }

    if (response.ok) {
      showRateDialog.value = false
      resetRateForm()
      await loadRates()
    } else {
      const errorData = await response.json()
      alert(errorData.errors?.join(', ') || t('rates.failedToSave'))
    }
  } catch (error) {
    console.error('Save rate error:', error)
    alert(t('rates.failedToSave'))
  }
}

const handleCoinChange = (coinId: number | null) => {
  const query = coinId ? { coin_id: coinId } : {}
  router.push({ path: '/rates', query })
  selectedCoinId.value = coinId
  loadRates()
}

const deleteRate = async (id: number) => {
  if (!confirm(t('rates.areYouSureDelete'))) return
  try {
    const response = await apiDelete(`/api/coin_rates/${id}`)
    if (response.ok) {
      await loadRates()
    }
  } catch (error) {
    console.error('Delete rate error:', error)
  }
}

onMounted(() => {
  // Initialize selectedCoinId from route query
  if (route.query.coin_id) {
    selectedCoinId.value = parseInt(route.query.coin_id as string)
  }
  loadCoins()
  loadRates()
})
</script>

<style scoped>
.coin-name-cell:hover {
  background-color: rgba(0, 0, 0, 0.05);
  text-decoration: underline;
}
.rates-table :deep(.v-table__wrapper) {
  overflow-x: visible;
}
</style>

